<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import { listActivity } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["activity", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? listActivity(selectedProjectId.value)
      : Promise.resolve([]),
  enabled: () => !!selectedProjectId.value,
  // The feed is a live record — refresh when the user returns to the tab.
  refetchOnWindowFocus: true,
});

function when(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}
</script>

<template>
  <PageLayout>
    <h1 class="text-xl font-semibold mb-1">
      Activity
    </h1>
    <p class="text-sm text-neutral-500 mb-6">
      Everything that has happened on this project, newest first — straight off
      the audit log.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="text-neutral-500"
    >
      No activity recorded on this project yet.
    </div>
    <ol
      v-else-if="data"
      class="relative ml-2 border-l border-neutral-200"
    >
      <li
        v-for="item in data"
        :key="item.id"
        class="relative pl-6 pb-6 last:pb-0"
      >
        <span
          class="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand-primary ring-4 ring-neutral-0"
        />
        <div class="flex items-baseline justify-between gap-4">
          <p class="font-medium text-neutral-900">
            {{ item.title }}
          </p>
          <time
            class="shrink-0 text-xs text-neutral-400"
            :title="new Date(item.occurred_at).toLocaleString('en-GB')"
          >
            {{ when(item.occurred_at) }}
          </time>
        </div>
        <p class="text-sm text-neutral-500">
          {{ item.actor_name }}
        </p>
        <p
          v-if="item.detail"
          class="mt-1 text-sm text-neutral-600"
        >
          {{ item.detail }}
        </p>
      </li>
    </ol>
  </PageLayout>
</template>
